import type { ParsedStatement, ParsedTransaction } from "@paisa/types"
import { parseSbiNarration } from "./savings"

// ── Column x-boundaries ──────────────────────────────────────────────────
// Derived from SBI PDF coordinate analysis.
const X_DATE_MAX = 60 // Date: x ≤ 60 (at x≈26)
const X_VALUEDT_MIN = 61 // Value Date: 61 ≤ x < 120 (at x≈78)
const X_VALUEDT_MAX = 120
const X_NARRATION_MIN = 120 // Narration: 120 ≤ x < 285 (at x≈130)
const X_NARRATION_MAX = 285
const X_DEBIT_MIN = 310 // Debit: 310 ≤ x < 395
const X_DEBIT_MAX = 395
const X_CREDIT_MIN = 395 // Credit: 395 ≤ x < 465
const X_CREDIT_MAX = 465
const X_BALANCE_MIN = 465 // Balance: x ≥ 465

// ── Y-coordinate boundaries ───────────────────────────────────────────────
const Y_FOOTER_MIN = 80 // y ≤ 80 = footer (page number), skip
const ROW_TOLERANCE = 4 // items within ±4 y-units are on the same logical row

// How far above a date row a narration label (e.g. "WDL TFR") can appear.
// In practice it's 6-8 y-units; we use 12 to be safe.
const NARRATION_ABOVE_TOLERANCE = 12

// ── Detect ───────────────────────────────────────────────────────────────

/**
 * Detect whether raw text extracted from a PDF looks like an SBI savings statement.
 */
export function isSbiSavingsPdf(rawContent: string): boolean {
  return (
    /State Bank of India/i.test(rawContent) &&
    /Statement From/i.test(rawContent) &&
    /WDL TFR|DEP TFR/i.test(rawContent)
  )
}

// ── Parse ────────────────────────────────────────────────────────────────

/**
 * Parse an SBI savings/current account PDF statement.
 *
 * @param data      Raw PDF file as ArrayBuffer
 * @param password  Optional PDF password (SBI typically uses DOB: "DDMMYYYY" or name+DOB)
 */
export function parseSbiSavingsItems(items: Coord[]): ParsedStatement {
  const metadata = extractMetadata(items)
  const transactions = parseTransactions(items)

  return {
    bank: "sbi",
    accountType: metadata.accountType,
    accountNumber: metadata.accountLast4,
    periodStart: metadata.periodStart,
    periodEnd: metadata.periodEnd,
    transactions,
  }
}

// ── Text extraction ───────────────────────────────────────────────────────

export interface Coord {
  page: number
  x: number
  y: number
  str: string
}

// ── Metadata extraction ───────────────────────────────────────────────────

interface StatementMetadata {
  accountType: "savings" | "current"
  accountLast4: string | null
  periodStart: string | null
  periodEnd: string | null
}

function extractMetadata(items: Coord[]): StatementMetadata {
  // Metadata lives on page 1 only
  const page1Items = items.filter((i) => i.page === 1)
  const rowStrings = groupIntoRowStrings(page1Items)
  const fullText = rowStrings.join(" ")

  let accountType: "savings" | "current" = "savings"
  let accountLast4: string | null = null
  let periodStart: string | null = null
  let periodEnd: string | null = null

  // "Account Number : 33471515974"
  const accountMatch = fullText.match(/Account Number\s*:\s*(\d{8,18})/)
  if (accountMatch) {
    accountLast4 = accountMatch[1].slice(-4)
  }

  // "Product : Savings Account" or "Current Account"
  if (/current.*account/i.test(fullText)) {
    accountType = "current"
  }

  // "Statement From : 01-04-2025 to 31-03-2026"
  const periodMatch = fullText.match(
    /Statement From\s*:\s*(\d{2}-\d{2}-\d{4})\s+to\s+(\d{2}-\d{2}-\d{4})/i,
  )
  if (periodMatch) {
    periodStart = parseSbiDate(periodMatch[1])
    periodEnd = parseSbiDate(periodMatch[2])
  }

  return { accountType, accountLast4, periodStart, periodEnd }
}

/** Group text items by (page, y) into single row strings (sorted left-to-right). */
function groupIntoRowStrings(items: Coord[]): string[] {
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null
    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      if (Math.abs(item.y - parseInt(key.slice(pagePrefix.length), 10)) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }
    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.values()].map((row) =>
    row
      .sort((a, b) => a.x - b.x)
      .map((c) => c.str)
      .join(" "),
  )
}

// ── Transaction parsing ───────────────────────────────────────────────────

interface TransactionBucket {
  page: number
  dateY: number
  dateStr: string
  valueDateStr: string | null
  debitStr: string | null
  creditStr: string | null
  balanceStr: string | null
  narrationParts: string[]
}

function parseTransactions(items: Coord[]): ParsedTransaction[] {
  // Drop footer items (page number row at y ≤ Y_FOOTER_MIN)
  const dataItems = items.filter((i) => i.y > Y_FOOTER_MIN)

  // ── Pass 1: find all transaction date rows ────────────────────────────
  const rows = buildRows(dataItems)

  // A date row has a DD/MM/YYYY item at x ≤ X_DATE_MAX
  const dateRows: { page: number; y: number; row: Coord[] }[] = []
  for (const row of rows) {
    const dateCell = row.find(
      (c) => c.x <= X_DATE_MAX && /^\d{2}\/\d{2}\/\d{4}$/.test(c.str.trim()),
    )
    if (dateCell) {
      dateRows.push({ page: dateCell.page, y: dateCell.y, row })
    }
  }

  // ── Pass 2: build transaction buckets from date rows ──────────────────
  const buckets = new Map<string, TransactionBucket>()

  for (const { page, y, row } of dateRows) {
    const key = `${page}:${y}`

    const dateCell = row.find((c) => c.x <= X_DATE_MAX)
    if (!dateCell) continue
    const valueDtCell = row.find((c) => c.x >= X_VALUEDT_MIN && c.x < X_VALUEDT_MAX)
    // Ignore "-" placeholders — they mark empty debit/credit columns
    const debitCell = row.find(
      (c) => c.x >= X_DEBIT_MIN && c.x < X_DEBIT_MAX && c.str.trim() !== "-",
    )
    const creditCell = row.find(
      (c) => c.x >= X_CREDIT_MIN && c.x < X_CREDIT_MAX && c.str.trim() !== "-",
    )
    const balanceCell = row.find((c) => c.x >= X_BALANCE_MIN)

    buckets.set(key, {
      page,
      dateY: y,
      dateStr: dateCell.str.trim(),
      valueDateStr: valueDtCell?.str.trim() ?? null,
      debitStr: debitCell?.str.trim() ?? null,
      creditStr: creditCell?.str.trim() ?? null,
      balanceStr: balanceCell?.str.trim() ?? null,
      narrationParts: [],
    })
  }

  // ── Pass 3: assign narration items to buckets ─────────────────────────
  // SBI narration wraps vertically around its date row:
  //   - The type label ("WDL TFR") appears ~6-8 y-units ABOVE the date row
  //   - Detail lines appear just BELOW the date row
  //
  // Rule: a narration item at (page, y_n) belongs to the date row with the
  // MAXIMUM y_d such that y_d ≤ y_n + NARRATION_ABOVE_TOLERANCE (same page).
  // This correctly captures both the label above and the details below.
  const narrationItems = dataItems.filter((i) => i.x >= X_NARRATION_MIN && i.x < X_NARRATION_MAX)

  for (const item of narrationItems) {
    // Find the nearest date row that is no more than NARRATION_ABOVE_TOLERANCE
    // y-units BELOW the narration item (or above it).
    //
    // Rule: minimum y_d such that y_d >= item.y - NARRATION_ABOVE_TOLERANCE (same page).
    //
    // Why this works:
    //   - The type label ("WDL TFR") appears ~6-8 y-units ABOVE its date row:
    //     y_label > y_date, and y_label - y_date ≤ NARRATION_ABOVE_TOLERANCE, so
    //     y_date >= y_label - NARRATION_ABOVE_TOLERANCE ✓
    //   - Detail lines below the date row: y_detail < y_date, so
    //     y_date >= y_detail - NARRATION_ABOVE_TOLERANCE (always true since y_date > y_detail)
    //     and we pick the MINIMUM y_d >= threshold, so we get the closest date row above ✓
    //   - Metadata items far above the first transaction have no qualifying date row (none
    //     is high enough on the page), so they're safely ignored ✓
    const yMin = item.y - NARRATION_ABOVE_TOLERANCE
    let bestKey: string | null = null
    let bestY = Number.MAX_SAFE_INTEGER

    for (const { page, y } of dateRows) {
      if (page !== item.page) continue
      if (y >= yMin && y < bestY) {
        bestY = y
        bestKey = `${page}:${y}`
      }
    }

    if (bestKey) {
      const bucket = buckets.get(bestKey)
      if (bucket) bucket.narrationParts.push(item.str)
    }
  }

  // ── Build transactions ────────────────────────────────────────────────
  // Sort document-order: page asc, y desc (top → bottom)
  const sortedBuckets = [...buckets.values()].sort((a, b) =>
    a.page !== b.page ? a.page - b.page : b.dateY - a.dateY,
  )

  const transactions: ParsedTransaction[] = []
  for (const bucket of sortedBuckets) {
    const txn = buildTransaction(bucket)
    if (txn) transactions.push(txn)
  }

  return transactions
}

/**
 * Group text items into rows.
 * Uses "page:y" composite bucket keys so items on different pages with the
 * same y-coordinate are never merged.
 * Sorted document-order: page asc, y desc (top → bottom).
 */
function buildRows(items: Coord[]): Coord[][] {
  const buckets = new Map<string, Coord[]>()

  for (const item of items) {
    const pagePrefix = `${item.page}:`
    let bucketKey: string | null = null

    for (const key of buckets.keys()) {
      if (!key.startsWith(pagePrefix)) continue
      const bucketY = parseInt(key.slice(pagePrefix.length), 10)
      if (Math.abs(item.y - bucketY) <= ROW_TOLERANCE) {
        bucketKey = key
        break
      }
    }

    if (bucketKey === null) {
      bucketKey = `${item.page}:${item.y}`
      buckets.set(bucketKey, [item])
    } else {
      const existing = buckets.get(bucketKey)
      if (existing) existing.push(item)
    }
  }

  return [...buckets.entries()]
    .sort((a, b) => {
      const [pa, ya] = a[0].split(":").map(Number)
      const [pb, yb] = b[0].split(":").map(Number)
      return pa !== pb ? pa - pb : yb - ya
    })
    .map(([, row]) => row.sort((r1, r2) => r1.x - r2.x))
}

function buildTransaction(bucket: TransactionBucket): ParsedTransaction | null {
  const date = parseSbiDate(bucket.dateStr)
  if (!date) return null

  // SBI narration lines end with a terminal reference ("0097690162095 AT 70766 BTM")
  // followed by a branch area name ("LAYOUT 2ND STAGE"). Keep everything up to and
  // including the terminal line; drop the branch area suffix.
  const terminalIdx = bucket.narrationParts.findIndex((p) => /^\d+\s+AT\s+\d+/.test(p.trim()))
  const parts =
    terminalIdx >= 0 ? bucket.narrationParts.slice(0, terminalIdx + 1) : bucket.narrationParts

  const narration = parts
    .join(" ")
    .replace(/\s{2,}/g, " ")
    .trim()
  if (!narration) return null

  const debit = parseAmount(bucket.debitStr)
  const credit = parseAmount(bucket.creditStr)
  if (debit === 0 && credit === 0) return null

  const amount = debit > 0 ? debit : credit
  const type: "debit" | "credit" = debit > 0 ? "debit" : "credit"

  const valueDate = bucket.valueDateStr ? parseSbiDate(bucket.valueDateStr) : null
  const closingBalance = parseAmount(bucket.balanceStr) || null

  const parsed = parseSbiNarration(narration)

  // Reference number is embedded in the narration, not a separate PDF column.
  // UPI: "UPI/DR/509617867713/..." → "509617867713"
  // NEFT/RTGS/IMPS: often a standalone numeric token near the end.
  const upiRefMatch = narration.match(/UPI\/(?:DR|CR)\/(\d+)\//i)
  const referenceNumber = upiRefMatch ? upiRefMatch[1] : null

  return {
    date,
    description: narration,
    amount,
    type,
    referenceNumber,
    valueDate,
    closingBalance,
    paymentMethod: parsed.paymentMethod,
    extractedName: parsed.name,
    upiId: parsed.upiId,
    sourceCategory: null,
  }
}

// ── Utilities ────────────────────────────────────────────────────────────

/**
 * Parse SBI date formats:
 *   "DD/MM/YYYY" → "YYYY-MM-DD"   (transaction rows)
 *   "DD-MM-YYYY" → "YYYY-MM-DD"   (metadata rows)
 */
function parseSbiDate(raw: string): string | null {
  if (!raw) return null
  const match = raw.trim().match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/)
  if (!match) return null
  return `${match[3]}-${match[2]}-${match[1]}`
}

function parseAmount(raw: string | null | undefined): number {
  if (!raw) return 0
  const cleaned = raw.replace(/,/g, "").trim()
  const num = parseFloat(cleaned)
  return Number.isNaN(num) ? 0 : Math.abs(num)
}
